import { Typography, Theme, Table, TableHead } from "@mui/material";
import { Domains, HeadingLevel } from "@/core/types/types";
import { format, parseISO } from "date-fns";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { getTopLevelDomain } from "@/core/utils";
import { dateFormats } from "@/core/data";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";

interface FlightSummaryTableProps {
  leadReferenceNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  departureDate: string;
  airlineName: string;
  flightProblem: string;
  customerFirstName: string;
  customerLastName: string;
  numberOfPassengers: number;
}

const styles = (theme: Theme) => ({
  table: {
    tableLayout: "fixed",
    width: "100%",
  },
  tableHead: {
    "& td > h6": {
      marginBottom: theme.spacing(2),
    },
  },
});

const FlightSummaryTable = ({
  leadReferenceNumber,
  departureAirport,
  arrivalAirport,
  departureDate,
  airlineName,
  flightProblem,
  customerFirstName,
  customerLastName,
  numberOfPassengers,
}: FlightSummaryTableProps) => {
  const domain: Domains = getTopLevelDomain() as Domains;
  const dateFormat: string = dateFormats[domain];
  const classes: any = useCustomStyles(styles);

  const rows = [
    { name: "label.referenceNumber", value: leadReferenceNumber },
    { name: "label.trip", value: `${departureAirport} - ${arrivalAirport}` },
    {
      name: "label.departureDate",
      value: departureDate ? format(parseISO(departureDate), dateFormat) : "",
    },
    { name: "label.airlineName", value: airlineName },
    {
      name: "label.flightProblem",
      value: flightProblem
        ? t(`flightProblem.${flightProblem.toLowerCase()}`)
        : "",
    },
    {
      name: "label.mainPassenger",
      value: `${customerFirstName} ${customerLastName}`,
    },
    { name: "label.numberOfPassengers", value: numberOfPassengers },
  ];

  const [tableHeader, ...tableBodyRows] = rows;

  return (
    <Table sx={classes?.table}>
      <TableHead sx={classes?.tableHead}>
        <tr>
          <td>
            <Typography variant={HeadingLevel.Subtitle1}>
              {t(tableHeader.name)}
            </Typography>
          </td>
          <td>
            <Typography variant={HeadingLevel.Subtitle1}>
              {tableHeader.value}
            </Typography>
          </td>
        </tr>
      </TableHead>
      <tbody>
        {tableBodyRows.map((row) => (
          <tr key={row.name}>
            <td>
              <Typography variant={HeadingLevel.Body2}>
                {t(row.name)}
              </Typography>
            </td>
            <td>
              <Typography variant={HeadingLevel.Body2}>{row.value}</Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export { FlightSummaryTable };
